import { Connection } from "@solana/web3.js";
import { resolveRpcUrl } from "@crunchdao/sdk";
import { loadKeypair } from "../utils/keys";
import { AnchorProvider, Wallet } from "@coral-xyz/anchor";

export class WalletLoadError extends Error {
  constructor(message: string, public cause?: Error) {
    super(message);
    this.name = "WalletLoadError";
  }
}

export const NETWORKS = {
  localnet: "http://127.0.0.1:8899",
  devnet: "https://api.devnet.solana.com",
  mainnet: "https://api.mainnet-beta.solana.com",
};

/**
 * Creates an Anchor provider with support for custom RPC URLs
 * @param networkOrUrl - Network name, moniker, or custom RPC URL
 * @param walletKeypairPath - Path to wallet keypair
 * @returns Configured AnchorProvider
 */
export function createAnchorProvider(networkOrUrl: string, walletKeypairPath: string) {
  let walletKeypair;
  try {
    walletKeypair = loadKeypair(walletKeypairPath);
  } catch (error) {
    throw new WalletLoadError(
      `Failed to load wallet keypair from ${walletKeypairPath}`,
      error as Error
    );
  }

  // First try legacy network resolution for backward compatibility
  let rpcUrl: string;
  if (networkOrUrl && NETWORKS[networkOrUrl as keyof typeof NETWORKS]) {
    rpcUrl = NETWORKS[networkOrUrl as keyof typeof NETWORKS];
  } else {
    // Use new RPC resolver for URLs and monikers
    rpcUrl = resolveRpcUrl(networkOrUrl);
  }

  const connection = new Connection(rpcUrl, "confirmed");
  const walletWrapper = new Wallet(walletKeypair);

  return new AnchorProvider(connection, walletWrapper, {
    preflightCommitment: "confirmed",
  });
}

/**
 * Legacy function for backward compatibility
 */
export function createAnchorProviderFromNetwork(network: string, walletKeypairPath: string) {
  return createAnchorProvider(network, walletKeypairPath);
}
